import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-3 -mx-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <div>
            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-primary-100 text-primary-700 mb-2">
              {article.category}
            </span>
            <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed hidden sm:block">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {(article.readCount / 10000).toFixed(1)}万
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3"
      >
        <div className="w-24 h-16 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-400 mt-1">{article.publishDate}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block"
    >
      <div className="overflow-hidden rounded-lg mb-3 aspect-[16/9]">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-primary-100 text-primary-700 mb-2">
        {article.category}
      </span>
      <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
        {article.title}
      </h3>
      <p className="text-sm text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
        {article.excerpt}
      </p>
      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
        <span>{article.publishDate}</span>
        <span className="flex items-center gap-1">
          <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
          {(article.readCount / 10000).toFixed(1)}万
        </span>
      </div>
    </Link>
  );
}